import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Space, Spin, Typography } from 'antd';
import { ArrowLeftOutlined, ToolOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useSalesOrder } from '@/hooks/useSalesOrder';
import { useConvertSalesOrderToWorkOrder } from '@/hooks/useWorkOrder';
import type { ConversionData } from '@/services/workOrder.service';
import salesOrderService from '@/services/salesOrder.service';
import type { SalesOrderItem } from '@/services/salesOrder.service';
import { SalesOrderToWorkOrderDialog } from '@/components/manufacturing';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';

const { Text } = Typography;

/**
 * Full-page flow: create work order(s) from a sales order (replaces modal-only UX for large forms).
 */
const SalesOrderToWorkOrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: order, isLoading, error } = useSalesOrder(id);
  const {
    data: lineItems = [],
    isLoading: itemsLoading,
    error: itemsError,
  } = useQuery({
    queryKey: ['sales-orders', id, 'items'],
    queryFn: () => salesOrderService.getOrderItems(id!),
    enabled: !!id,
  });
  const convertMutation = useConvertSalesOrderToWorkOrder();

  const itemsForConversion = useMemo(
    () =>
      lineItems.filter(
        (it): it is SalesOrderItem & { id: string } =>
          typeof it.id === 'string' && it.id.trim().length > 0 && it.id.trim().length <= 36
      ),
    [lineItems]
  );

  const breadcrumbSegments = useMemo(
    () =>
      order
        ? [
            { title: 'Growth & Revenue', path: '/growth/dashboard' },
            { title: 'Customer orders', path: '/growth/orders' },
            { title: order.sales_order_number || 'Order', path: `/growth/orders/${order.id}` },
            { title: 'Create work order' },
          ]
        : null,
    [order?.id, order?.sales_order_number]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  if (!id) {
    return null;
  }

  if (isLoading || itemsLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Unable to load customer order.</Text>
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/orders')}>
            Back to orders
          </Button>
        </div>
      </div>
    );
  }

  if (order.order_status === 'Cancelled') {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Cannot create work orders from a cancelled customer order.</Text>
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/orders')}>
            Back to orders
          </Button>
        </div>
      </div>
    );
  }

  const missingCustomerPo =
    !String(order.customer_po_number ?? '').trim() || !String(order.customer_po_date ?? '').trim();

  if (missingCustomerPo) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">
          Cannot create work orders until Customer PO and Customer PO date are set on the customer order.
        </Text>
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/growth/orders/${order.id}/edit`)}>
            Edit customer order
          </Button>
          <Button style={{ marginLeft: 8 }} icon={<ArrowLeftOutlined />} onClick={() => navigate(`/growth/orders/${order.id}`)}>
            Back to customer order
          </Button>
        </div>
      </div>
    );
  }

  if (order.approval_status !== 'Approved' || order.sales_review_status !== 'reviewed') {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">
          Cannot create work orders until the customer order is fully approved.
        </Text>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            Current status: approval {order.approval_status || '—'}, sales review {order.sales_review_status || '—'}.
          </Text>
        </div>
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/growth/orders/${order.id}`)}>
            Back to customer order
          </Button>
        </div>
      </div>
    );
  }

  if (itemsError) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Unable to load customer order line items.</Text>
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/growth/orders/${order.id}`)}>
            Back to customer order
          </Button>
        </div>
      </div>
    );
  }

  if (itemsForConversion.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="warning">
          No customer order lines with valid IDs were returned. Refresh the page or ensure migration/data is consistent so
          each line has a persisted id.
        </Text>
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/growth/orders/${order.id}`)}>
            Back to customer order
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ margin: '-20px -20px 0', marginBottom: 0 }}>
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Manufacturing"
            documentNumber={order.sales_order_number}
            subtitle={
              <Space>
                <ToolOutlined />
                <Text type="secondary">Create work order from customer order</Text>
              </Space>
            }
            primaryActions={
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/growth/orders/${order.id}`)}>
                Back to customer order
              </Button>
            }
          />
        }
      >
        <Card bordered={false} style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SalesOrderToWorkOrderDialog
            variant="page"
            salesOrderId={order.id}
            salesOrderNumber={order.sales_order_number}
            items={itemsForConversion}
            onClose={() => navigate(`/growth/orders/${order.id}`)}
            onSubmit={async (data: ConversionData) => {
              await convertMutation.mutateAsync({ salesOrderId: order.id, data });
            }}
            onSuccess={() => navigate('/mfg/work-orders')}
          />
        </Card>
      </WorkspaceLayout>
    </div>
  );
};

export default SalesOrderToWorkOrderPage;
